/**
 * Modal reader for What’s New changelog entries.
 * One entry at a time; Next advances; last note’s primary dismisses.
 * Can be mounted on any page (creates overlay if missing).
 */

import {
  markWhatsNewCaughtUp,
  type WhatsNewEntry,
} from "./whatsNew";

export type WhatsNewDialogOptions = {
  entries: readonly WhatsNewEntry[];
  /** 0-based index to open on (default 0). */
  startIndex?: number;
  /**
   * When true, dismissing marks the catalog as caught up
   * (home “unseen updates” flow). Settings re-reads leave this false.
   */
  markCaughtUpOnClose?: boolean;
  /** Optional label for the close button (default “Close”). */
  closeLabel?: string;
  onClose?: () => void;
};

type Session = {
  entries: WhatsNewEntry[];
  index: number;
  markCaughtUpOnClose: boolean;
  closeLabel: string;
  onClose?: () => void;
};

let session: Session | null = null;
let wired = false;

function ensureOverlay(): HTMLElement {
  let overlay = document.getElementById("whats-new-overlay");
  if (overlay) return overlay;

  overlay = document.createElement("div");
  overlay.id = "whats-new-overlay";
  overlay.className = "whats-new-overlay hidden";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "whats-new-title");
  overlay.hidden = true;
  overlay.innerHTML = `
    <div class="whats-new-card">
      <p class="whats-new-kicker" id="whats-new-kicker">What’s new</p>
      <h2 id="whats-new-title" class="whats-new-title" aria-live="polite">Updates</h2>
      <div id="whats-new-body" class="whats-new-body"></div>
      <div class="whats-new-actions">
        <button type="button" class="btn secondary" id="btn-whats-new-prev" hidden>
          ← Previous
        </button>
        <button type="button" class="btn secondary" id="btn-whats-new-close">
          Close
        </button>
        <button type="button" class="btn primary" id="btn-whats-new-next">
          Next
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  return overlay;
}

function wireOnce(): void {
  if (wired) return;
  wired = true;
  const overlay = ensureOverlay();

  document
    .getElementById("btn-whats-new-close")
    ?.addEventListener("click", () => closeWhatsNewDialog());

  document
    .getElementById("btn-whats-new-prev")
    ?.addEventListener("click", () => prevWhatsNewEntry());

  document
    .getElementById("btn-whats-new-next")
    ?.addEventListener("click", () => nextWhatsNewEntry());

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeWhatsNewDialog();
  });

  window.addEventListener("keydown", (e) => {
    if (!session) return;
    const el = document.getElementById("whats-new-overlay");
    if (!el || el.hidden || el.classList.contains("hidden")) return;

    if (e.key === "Escape") {
      e.preventDefault();
      closeWhatsNewDialog();
      return;
    }
    if (e.key === "ArrowRight") {
      const nextBtn = document.getElementById(
        "btn-whats-new-next",
      ) as HTMLButtonElement | null;
      if (nextBtn && !nextBtn.hidden && !nextBtn.disabled) {
        e.preventDefault();
        nextWhatsNewEntry();
      }
    }
    if (e.key === "ArrowLeft") {
      const prevBtn = document.getElementById(
        "btn-whats-new-prev",
      ) as HTMLButtonElement | null;
      if (prevBtn && !prevBtn.hidden && !prevBtn.disabled) {
        e.preventDefault();
        prevWhatsNewEntry();
      }
    }
  });
}

function actionButtons(): {
  prev: HTMLButtonElement | null;
  close: HTMLButtonElement | null;
  next: HTMLButtonElement | null;
} {
  return {
    prev: document.getElementById(
      "btn-whats-new-prev",
    ) as HTMLButtonElement | null,
    close: document.getElementById(
      "btn-whats-new-close",
    ) as HTMLButtonElement | null,
    next: document.getElementById(
      "btn-whats-new-next",
    ) as HTMLButtonElement | null,
  };
}

function setBtnVisible(btn: HTMLButtonElement | null, visible: boolean): void {
  if (!btn) return;
  btn.hidden = !visible;
  btn.disabled = !visible;
}

function renderCurrent(): void {
  if (!session || !session.entries.length) return;
  const { entries, index, closeLabel } = session;
  const entry = entries[index]!;
  const n = entries.length;
  const isFirst = index <= 0;
  const isLast = index >= n - 1;

  const kicker = document.getElementById("whats-new-kicker");
  const titleEl = document.getElementById("whats-new-title");
  const body = document.getElementById("whats-new-body");
  const { prev: prevBtn, close: closeBtn, next: nextBtn } = actionButtons();

  if (kicker) {
    kicker.textContent =
      n > 1 ? `What’s new · ${index + 1} of ${n}` : "What’s new";
  }
  if (titleEl) titleEl.textContent = entry.title;

  if (body) {
    body.replaceChildren();
    const block = document.createElement("article");
    block.className = "whats-new-entry";
    if (entry.date) {
      const d = document.createElement("p");
      d.className = "whats-new-entry-date";
      d.textContent = entry.date;
      block.appendChild(d);
    }
    const ul = document.createElement("ul");
    for (const line of entry.bullets) {
      const li = document.createElement("li");
      li.textContent = line;
      ul.appendChild(li);
    }
    block.appendChild(ul);
    body.appendChild(block);
    body.scrollTop = 0;
  }

  // Close stays available on every note (settings re-read + skip remaining).
  // On the last / only note it becomes the primary “Got it”.
  setBtnVisible(prevBtn, n > 1 && !isFirst);
  if (prevBtn && n > 1 && !isFirst) {
    prevBtn.title = `Previous update (${index} of ${n})`;
  }

  const closeIsPrimary = n <= 1 || isLast;
  if (closeBtn) {
    setBtnVisible(closeBtn, true);
    closeBtn.textContent = closeIsPrimary && n > 1 ? "Got it" : closeLabel;
    closeBtn.classList.toggle("primary", closeIsPrimary);
    closeBtn.classList.toggle("secondary", !closeIsPrimary);
    closeBtn.title = closeIsPrimary ? "Dismiss" : closeLabel;
  }

  setBtnVisible(nextBtn, n > 1 && !isLast);
  if (nextBtn && n > 1 && !isLast) {
    nextBtn.textContent = "Next →";
    nextBtn.title = `Next update (${index + 2} of ${n})`;
  }
}

function focusPrimaryAction(): void {
  const { close: closeBtn, next: nextBtn } = actionButtons();
  if (nextBtn && !nextBtn.hidden) {
    nextBtn.focus();
    return;
  }
  closeBtn?.focus();
}

export function isWhatsNewDialogOpen(): boolean {
  const el = document.getElementById("whats-new-overlay");
  return !!session && !!el && !el.hidden && !el.classList.contains("hidden");
}

/**
 * Open the dialog for one or more changelog entries.
 * Shows a single entry at a time; Next advances, last note dismisses.
 */
export function openWhatsNewDialog(opts: WhatsNewDialogOptions): void {
  const list = opts.entries.filter(Boolean);
  if (!list.length) return;

  ensureOverlay();
  wireOnce();

  const start = Math.max(
    0,
    Math.min(opts.startIndex ?? 0, list.length - 1),
  );
  session = {
    entries: [...list],
    index: start,
    markCaughtUpOnClose: opts.markCaughtUpOnClose === true,
    closeLabel: opts.closeLabel ?? "Close",
    onClose: opts.onClose,
  };

  renderCurrent();

  const overlay = document.getElementById("whats-new-overlay")!;
  overlay.hidden = false;
  overlay.classList.remove("hidden");

  window.setTimeout(() => {
    focusPrimaryAction();
  }, 50);
}

export function nextWhatsNewEntry(): void {
  if (!session || session.index >= session.entries.length - 1) return;
  session.index += 1;
  renderCurrent();
  focusPrimaryAction();
}

export function prevWhatsNewEntry(): void {
  if (!session || session.index <= 0) return;
  session.index -= 1;
  renderCurrent();
  const { prev, close } = actionButtons();
  if (prev && !prev.hidden) prev.focus();
  else close?.focus();
}

export function closeWhatsNewDialog(): void {
  if (!session) {
    const overlay = document.getElementById("whats-new-overlay");
    if (overlay) {
      overlay.classList.add("hidden");
      overlay.hidden = true;
    }
    return;
  }

  const { markCaughtUpOnClose, onClose } = session;
  session = null;

  if (markCaughtUpOnClose) markWhatsNewCaughtUp();

  const overlay = document.getElementById("whats-new-overlay");
  if (overlay) {
    overlay.classList.add("hidden");
    overlay.hidden = true;
  }
  onClose?.();
}
